import BugsnagPerformance, { type BrowserConfiguration } from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import { VueRouterRoutingProvider } from '@bugsnag/vue-router-performance';
import sanitizeUrl from 'utils/sanitize-url';
import type { Router } from 'vue-router';

interface ConfigureBugsnagPerformanceOptions {
	apiKey: string;
	appVersion: string;
	enabledReleaseStages: string[];
	releaseStage: string;
	router: Router;
	serviceName: string;
}

type NetworkRequestInfo = Parameters<NonNullable<BrowserConfiguration['networkRequestCallback']>>[0];

/**
 * Keeps network performance grouping useful without sending credentials, query
 * parameters or fragments, which can contain Creator access codes, user tokens,
 * or customer data.
 *
 * @param requestInfo - The network request details captured by the browser SDK.
 * @returns A copy safe for telemetry, reduced to the request origin and path.
 */
function sanitizeNetworkRequest(
	requestInfo: NetworkRequestInfo,
): NetworkRequestInfo {
	return {
		...requestInfo,
		propagateTraceContext: false,
		url: (
			requestInfo.url
			&& sanitizeUrl(requestInfo.url)
		),
	};
}

/**
 * Starts BugSnag real-user performance monitoring against Creator's existing
 * error project and Vue Router. The SDK's default automatic instrumentation
 * already covers full page loads, route changes, resource loads, and fetch/XHR
 * requests, so only Creator's deviations from the defaults are set here.
 *
 * @param options - Existing BugSnag release metadata plus Creator's router.
 */
export function configureBugsnagPerformance(
	options: ConfigureBugsnagPerformanceOptions,
): void {
	BugsnagPerformance.start({
		apiKey: options.apiKey,
		appVersion: options.appVersion,
		bugsnag: Bugsnag,
		enabledReleaseStages: options.enabledReleaseStages,
		networkRequestCallback: sanitizeNetworkRequest,
		releaseStage: options.releaseStage,
		routingProvider: new VueRouterRoutingProvider(
			options.router,
			options.router.options.history.base,
		),
		sendPageAttributes: {
			referrer: false,
			url: false,
		},
		serviceName: options.serviceName,
	});
}
